import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Loader2, ImagePlus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/data";
import { Eyebrow } from "@/components/site/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INPUT =
  "rounded-none border-[#2a2a2a] bg-[#121212] text-[#f5f5f0] placeholder:text-[#5a5a5a] focus-visible:ring-0 focus-visible:border-[#c4a962] h-12";
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PHOTOS = 6;

const DETAILS = [
  { icon: Phone, label: "24/7 Emergency line", value: "0407 051 043", href: "tel:0407051043" },
  { icon: Mail, label: "Email", value: "info@jmvmanagement.com.au", href: "mailto:info@jmvmanagement.com.au" },
  { icon: MapPin, label: "Service area", value: "Victoria & New South Wales (within a 350km radius of Swan Hill)" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", location: "",
    urgency: "standard", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`You can attach up to ${MAX_PHOTOS} photos.`);
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (!ACCEPT.includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG or WEBP allowed.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is larger than 10MB.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [
          ...p,
          { id: data.id, path: data.path, filename: data.filename, url: `${BACKEND_URL}${data.url}` },
        ]);
      } catch {
        toast.error(`Could not upload ${file.name}.`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, { ...form, photos });
      toast.success("Enquiry received. We'll be in touch shortly.");
      // Google tag: record the lead / quote-form submission
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "enquiry",
          event_label: form.service || "Quote request",
          value: 1,
        });
        // Google Ads conversion — activates once your conversion label is added:
        // window.gtag("event", "conversion", { send_to: "AW-18358574992/YOUR_LABEL" });
      }
      setForm({ name: "", email: "", phone: "", service: "", location: "", urgency: "standard", message: "" });
      setPhotos([]);
    } catch {
      toast.error("Something went wrong. Please call us on 0407 051 043.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-20 border-t border-[#2a2a2a] bg-[#050505] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-10">
          {/* Left */}
          <div className="md:col-span-5">
            <Eyebrow>Need support?</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.02] tracking-tighter text-[#f5f5f0] sm:text-5xl lg:text-6xl">
              Request a scope & quote.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#8a8a8a]">
              We can attend site, provide a clear scope of works based on
              assessment, and supply a quote with appropriate documentation to
              support your decision making.
            </p>

            <div className="mt-10 flex flex-col">
              {DETAILS.map((d) => {
                const Icon = d.icon;
                const inner = (
                  <div className="group flex items-center gap-4 border-t border-[#1a1a1a] py-5">
                    <Icon className="h-5 w-5 text-[#c4a962]" strokeWidth={1.6} />
                    <div>
                      <div className="text-xs uppercase tracking-wide text-[#8a8a8a]">
                        {d.label}
                      </div>
                      <div className="font-display text-lg tracking-tight text-[#f5f5f0] transition-colors group-hover:text-[#c4a962]">
                        {d.value}
                      </div>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a key={d.label} href={d.href} data-testid={`contact-${d.label}`}>
                    {inner}
                  </a>
                ) : (
                  <div key={d.label}>{inner}</div>
                );
              })}
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            data-testid="enquiry-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="border border-[#2a2a2a] bg-[#0a0a0a] p-6 md:col-span-7 md:p-10"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Full name *">
                <Input data-testid="input-name" className={INPUT} value={form.name} onChange={set("name")} placeholder="Your name" />
              </Field>
              <Field label="Phone *">
                <Input data-testid="input-phone" className={INPUT} value={form.phone} onChange={set("phone")} placeholder="Contact number" />
              </Field>
              <Field label="Email *">
                <Input data-testid="input-email" type="email" className={INPUT} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </Field>
              <Field label="Location">
                <Input data-testid="input-location" className={INPUT} value={form.location} onChange={set("location")} placeholder="Suburb / town" />
              </Field>
              <Field label="Service required *">
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="select-service" className={INPUT}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[#2a2a2a] bg-[#121212] text-[#f5f5f0]">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.title} className="focus:bg-[#1a1a1a] focus:text-[#c4a962]">
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other / not sure" className="focus:bg-[#1a1a1a] focus:text-[#c4a962]">
                      Other / not sure
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Urgency">
                <Select value={form.urgency} onValueChange={set("urgency")}>
                  <SelectTrigger data-testid="select-urgency" className={INPUT}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[#2a2a2a] bg-[#121212] text-[#f5f5f0]">
                    <SelectItem value="standard" className="focus:bg-[#1a1a1a] focus:text-[#c4a962]">Standard</SelectItem>
                    <SelectItem value="emergency" className="focus:bg-[#1a1a1a] focus:text-[#c4a962]">Emergency (24/7)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-6">
              <Field label="How can we help? *">
                <Textarea data-testid="input-message" rows={5} className={`${INPUT} h-auto py-3`} value={form.message} onChange={set("message")} placeholder="Briefly describe the situation and property." />
              </Field>
            </div>

            <div className="mt-6">
              <Field label="Attach photos (optional)">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  data-testid="photo-input"
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <button
                  type="button"
                  data-testid="photo-dropzone"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                  className="flex w-full flex-col items-center justify-center gap-2 border border-dashed border-[#2a2a2a] bg-[#121212] px-6 py-8 text-center transition-colors duration-300 hover:border-[#c4a962]"
                >
                  {uploading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-[#c4a962]" />
                  ) : (
                    <ImagePlus className="h-6 w-6 text-[#c4a962]" strokeWidth={1.6} />
                  )}
                  <span className="text-sm text-[#f5f5f0]">
                    {uploading ? "Uploading…" : "Click to upload or drag photos here"}
                  </span>
                  <span className="text-xs text-[#5a5a5a]">
                    JPG, PNG or WEBP · up to 10MB each · max {MAX_PHOTOS}
                  </span>
                </button>

                {photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="photo-previews">
                    {photos.map((p) => (
                      <div key={p.id} className="group relative aspect-square overflow-hidden border border-[#2a2a2a]">
                        <img src={p.url} alt={p.filename} className="h-full w-full object-cover" />
                        <button
                          type="button"
                          data-testid={`remove-photo-${p.id}`}
                          onClick={() => removePhoto(p.id)}
                          className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-[#050505]/80 text-[#f5f5f0] transition-colors duration-200 hover:bg-[#8B0000]"
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
            </div>

            <Button
              type="submit"
              disabled={loading || uploading}
              data-testid="submit-enquiry-btn"
              className="mt-8 flex h-14 w-full items-center justify-center gap-3 rounded-none bg-[#f5f5f0] text-sm font-semibold uppercase tracking-wide text-[#050505] transition-colors duration-300 hover:bg-[#c4a962] disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
              ) : (
                <>Send enquiry <ArrowRight className="h-5 w-5" /></>
              )}
            </Button>
            <p className="mt-4 text-center text-xs text-[#5a5a5a]">
              For urgent matters, please call our emergency response line. All
              email enquiries will be attended to within 24 hours.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-[#8a8a8a]">
      {label}
    </Label>
    {children}
  </div>
);
